import React, { useState, useEffect, useRef } from 'react';
import { Flame, Lock } from 'lucide-react';
import BuiltInFormRenderer from '../DynamicForm/BuiltInFormRenderer';
import { useRetrofitPhotoUpload } from '../../hooks/useRetrofitPhotoUpload';
import { getRetrofitSurveyAddress } from '../../utils/retrofitSurveyStorage';
import PhotoGalleryModal from '../PhotoGalleryModal';
import PCDBSearch, { PCDBHeatingSystem } from '../PCDBSearch';
import { heatingSystemSchema } from '../../schemas/heatingSystemSchema';
import type { FieldSchema } from '../../types/formSchema';
import { deletePhotoFromSupabase } from '../../utils/supabaseStorage';

interface HeatingSystemFormSchemaVersionProps {
  surveyData: any;
  onDataUpdate: (updater: (prev: any) => any, section: string) => void;
  customFields: FieldSchema[];
  hiddenFields: string[];
  hiddenSections: string[];
  sectionOrder: string[];
  builtInFieldOrder: string[];
  onNavigateToPrevious?: () => void;
  onNavigateToNext?: () => void;
}

export const HeatingSystemFormSchemaVersion: React.FC<HeatingSystemFormSchemaVersionProps> = ({
  surveyData,
  onDataUpdate,
  customFields,
  hiddenFields,
  hiddenSections,
  sectionOrder,
  builtInFieldOrder,
  onNavigateToPrevious,
  onNavigateToNext,
}) => {
  const surveyId = surveyData?.id;
  
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [photos, setPhotos] = useState<Record<string, any[]>>({});
  const [customFieldData, setCustomFieldData] = useState<Record<string, any>>({});
  const [selectedGallery, setSelectedGallery] = useState<string | null>(null);
  
  // PCDB search state
  const [selectedPCDBSystem, setSelectedPCDBSystem] = useState<PCDBHeatingSystem | null>(null);
  const [isManualEntry, setIsManualEntry] = useState(false);
  
  const isInternalUpdate = useRef(false);
  // Initialize photo upload queue with explicit surveyId and property name for better file organization
  const propertyName = surveyData ? getRetrofitSurveyAddress(surveyData) : undefined;
  const { queuePhotoUpload } = useRetrofitPhotoUpload(surveyId, propertyName);
  
  // Helper to detect if current data is from PCDB
  const hasValidPCDBData = () => {
    if (selectedPCDBSystem) return true;
    return !!(formData.boilerManufacturer && formData.boilerBrand && formData.sapWinterEfficiency);
  };
  
  // PCDB-controlled fields that should be read-only when PCDB data is present
  const pcdbControlledFields = [
    'boilerManufacturer',
    'boilerBrand',
    'modelQualifier',
    'boilerType',
    'fuelType',
    'sapWinterEfficiency',
    'commissionYear',
    'serialNumber'
  ];

  // Load built-in form data
  useEffect(() => {
    if (isInternalUpdate.current) {
      console.log('[HeatingSystem] Skipping main data reload - internal update');
      return;
    }
    
    if (surveyData?.heating_system) {
      console.log('[HeatingSystem] Loading built-in data from surveyData');
      const sectionData = surveyData.heating_system;
      
      const newFormData: Record<string, any> = {};
      const newPhotos: Record<string, any[]> = {};
      
      Object.entries(sectionData).forEach(([key, value]) => {
        // Skip photos object - we'll handle it separately
        if (key === 'photos') return;
        
        // Check if it's a photo field
        const schemaField = heatingSystemSchema.sections
          .flatMap(s => s.fields)
          .find(f => f.id === key);
        
        if (schemaField?.type === 'photo' && Array.isArray(value)) {
          newPhotos[key] = value;
        } else if (value !== undefined && value !== null) {
          newFormData[key] = value;
        }
      });
      
      // Also load from photos object if it exists (backward compatibility)
      if (sectionData.photos) {
        Object.entries(sectionData.photos).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            newPhotos[key] = value;
          }
        });
      }
      
      setFormData(newFormData);
      setPhotos(newPhotos);
      
      // Restore PCDB selection if it exists
      if (sectionData.selectedPCDBSystem) {
        setSelectedPCDBSystem(sectionData.selectedPCDBSystem);
        setIsManualEntry(false);
        console.log('[HeatingSystem] Restored PCDB selection');
      }
    }
  }, [surveyData]);
  
  // Load custom field data
  useEffect(() => {
    if (isInternalUpdate.current) {
      console.log('[HeatingSystem] Skipping custom field reload - internal update');
      return;
    }
    
    if (surveyData?.heating_system && customFields && customFields.length > 0) {
      console.log('[HeatingSystem] Loading custom field data');
      const existingData: Record<string, any> = {};
      
      customFields.forEach(field => {
        const value = (surveyData.heating_system as any)?.[field.id];
        if (value !== undefined) {
          existingData[field.id] = value;
        }
        
        const photosFieldId = `${field.id}_photos`;
        const photoValue = (surveyData.heating_system as any)?.[photosFieldId];
        if (photoValue !== undefined) {
          console.log('[HeatingSystem] Loading photos for custom field:', field.id, '→', photosFieldId);
          existingData[photosFieldId] = photoValue;
        }
      });
      
      if (Object.keys(existingData).length > 0) {
        console.log('[HeatingSystem] Loaded custom field data:', Object.keys(existingData));
        setCustomFieldData(existingData);
      }
    }
  }, [surveyData?.heating_system, customFields]);
  
  // Reset internal update flag after all data loaders have run
  useEffect(() => {
    if (isInternalUpdate.current) {
      console.log('[HeatingSystem] Resetting internal update flag');
      isInternalUpdate.current = false;
    }
  }, [surveyData]);
  
  // Handle PCDB system selection
  const handlePCDBSystemSelect = (system: PCDBHeatingSystem | null) => {
    setSelectedPCDBSystem(system);
    
    if (system) {
      console.log('[HeatingSystem] PCDB system selected:', system);
      const updatedData = {
        boilerManufacturer: system.brand || '',
        boilerBrand: system.model || '',
        modelQualifier: system.model_qualifier || '',
        boilerType: system.main_type || '',
        fuelType: system.fuel || '',
        sapWinterEfficiency: system.sap_winter_seasonal_efficiency?.toString() || '',
        commissionYear: system.year_of_commission || '',
        serialNumber: system.pcdb_reference || ''
      };
      
      setFormData(prev => ({ ...prev, ...updatedData }));
      
      if (onDataUpdate) {
        setTimeout(() => {
          isInternalUpdate.current = true;
          onDataUpdate((prevData) => ({
            ...prevData,
            heating_system: {
              ...prevData.heating_system,
              ...updatedData,
              selectedPCDBSystem: system,
              photos,
              ...customFieldData
            }
          }), 'heating_system');
        }, 50);
      }
    } else {
      console.log('[HeatingSystem] PCDB selection cleared');
      if (onDataUpdate) {
        setTimeout(() => {
          isInternalUpdate.current = true;
          onDataUpdate((prevData) => ({
            ...prevData,
            heating_system: {
              ...prevData.heating_system,
              selectedPCDBSystem: null,
              photos,
              ...customFieldData
            }
          }), 'heating_system');
        }, 50);
      }
    }
  };
  
  const handleManualEntryChange = (manual: boolean) => {
    setIsManualEntry(manual);
    if (manual) {
      setSelectedPCDBSystem(null);
      console.log('[HeatingSystem] Manual entry mode enabled');
    }
  };
  
  // Handle form data changes
  const handleFormDataChange = (fieldId: string, value: any) => {
    // Block changes to PCDB-controlled fields when PCDB data is active
    if (!isManualEntry && hasValidPCDBData() && pcdbControlledFields.includes(fieldId)) {
      console.warn('[HeatingSystem] Blocked edit to PCDB-controlled field:', fieldId);
      return;
    }
    
    setFormData(prev => {
      const updated = { ...prev, [fieldId]: value };
      
      if (onDataUpdate) {
        setTimeout(() => {
          isInternalUpdate.current = true;
          onDataUpdate((prevData) => ({
            ...prevData,
            heating_system: {
              ...prevData.heating_system,
              ...updated,
              photos,
              ...customFieldData
            }
          }), 'heating_system');
        }, 50);
      }
      
      return updated;
    });
  };
  
  const handleCustomFieldChange = (fieldId: string, value: any) => {
    setCustomFieldData(prev => {
      const updated = { ...prev, [fieldId]: value };
      
      if (onDataUpdate) {
        setTimeout(() => {
          isInternalUpdate.current = true;
          onDataUpdate((prevData) => ({
            ...prevData,
            heating_system: {
              ...prevData.heating_system,
              ...formData,
              photos,
              ...updated
            }
          }), 'heating_system');
        }, 50);
      }
      
      return updated;
    });
  };
  
  const handlePhotosChange = (fieldId: string, newPhotos: any[]) => {
    console.log(`[HeatingSystem] Handling photos change for ${fieldId}:`, newPhotos);
    
    const newPhotoData = newPhotos.map(photo => {
      let dataUrl = photo.dataUrl || photo.base64String;
      
      if (dataUrl && !dataUrl.startsWith('data:')) {
        const format = photo.format || 'jpeg';
        dataUrl = `data:image/${format};base64,${dataUrl}`;
      }
      
      return {
        id: photo.id || `photo_${Date.now()}_${Math.random()}`,
        displayId: photo.displayId || photo.id,
        dataUrl,
        url: photo.url || '',
        uploading: false,
        format: photo.format || 'jpeg',
      };
    });
    
    setPhotos(prev => {
      const updated = { ...prev, [fieldId]: newPhotoData };
      
      if (onDataUpdate) {
        isInternalUpdate.current = true;
        onDataUpdate((prevData) => ({
          ...prevData,
          heating_system: {
            ...prevData.heating_system,
            ...formData,
            photos: updated,
            ...customFieldData
          }
        }), 'heating_system');
      }
      
      return updated;
    });
    
    newPhotoData.forEach((photo) => {
      if (photo.dataUrl && !photo.url) {
        queuePhotoUpload(
          photo.displayId,
          photo.dataUrl,
          'heating_system',
          fieldId,
          (url: string) => {
            setPhotos(prev => ({
              ...prev,
              [fieldId]: (prev[fieldId] || []).map(p =>
                p.id === photo.id ? { ...p, url, uploading: false } : p
              ),
            }));
          },
          (error: string) => console.error('Photo upload failed:', error)
        );
      }
    });
  };
  
  const handleGalleryOpen = (fieldId: string) => {
    setSelectedGallery(fieldId);
  };
  
  const handleGalleryClose = () => {
    setSelectedGallery(null);
  };

  // Remove photo function
  const removePhoto = async (photoType: string, displayIdToRemove: string) => {
    console.log(`[HeatingSystem] 🗑️ Removing photo from ${photoType}, displayId: ${displayIdToRemove}`);
    
    const currentPhotos = photos[photoType] || [];
    const photoToDelete = currentPhotos.find((p: any) => (p.displayId || p.id) === displayIdToRemove);
    
    if (photoToDelete) {
      let filePathToDelete = photoToDelete.fileName;
      
      if (!filePathToDelete && photoToDelete.url && photoToDelete.url.includes('/storage/v1/object/public/')) {
        const urlParts = photoToDelete.url.split('/storage/v1/object/public/')[1];
        if (urlParts) {
          const [bucket, ...pathParts] = urlParts.split('/');
          filePathToDelete = pathParts.join('/');
        }
      }
      
      if (filePathToDelete) {
        try {
          await deletePhotoFromSupabase(filePathToDelete, 'retrofit-survey-images');
          console.log(`[HeatingSystem] ✅ Deleted from storage`);
        } catch (error) {
          console.warn(`[HeatingSystem] ⚠️ Failed to delete from storage:`, error);
        }
      }
    }
    
    const filteredPhotos = currentPhotos.filter((p: any) => (p.displayId || p.id) !== displayIdToRemove);
    setPhotos(prev => ({ ...prev, [photoType]: filteredPhotos }));
    
    if (onDataUpdate) {
      onDataUpdate((prevData: any) => ({
        ...prevData,
        heating_system: {
          ...prevData.heating_system,
          photos: {
            ...photos,
            [photoType]: filteredPhotos
          }
        }
      }), 'heating_system');
    }
  };
  
  const handleSubmit = (data: Record<string, any>) => {
    console.log('[HeatingSystem] Form submitted:', data);
  };
  
  const allFormData = { ...formData, ...customFieldData };
  
  // Show PCDB search when gas or oil boiler is selected
  const showPCDBSearch = formData.mainHeatingSource === 'gas_boiler' || formData.mainHeatingSource === 'oil_boiler';

  return (
    <>
      {/* PCDB Search Section */}
      {showPCDBSearch && (
        <div className="mb-6 space-y-3 bg-white/80 backdrop-blur-sm p-6 rounded-xl shadow-lg border border-yellow-200 relative z-50">
          <div className="flex items-center gap-2 text-sm text-gray-600 bg-blue-50 p-3 rounded-lg border border-blue-200">
            <span className="font-medium">💡 Tip:</span>
            <span>Search the PCDB database to auto-fill boiler details, or toggle manual entry to fill fields yourself.</span>
          </div>
          <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50/30">
            <PCDBSearch
              onSelect={handlePCDBSystemSelect}
              selectedSystem={selectedPCDBSystem}
              placeholder="Search PCDB for boiler (brand, model, GC number)..."
              onManualEntryChange={handleManualEntryChange}
              isManualEntry={isManualEntry}
            />
          </div>
          {!isManualEntry && hasValidPCDBData() && (
            <div className="flex items-center gap-2 text-sm bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800">
              <Lock className="w-4 h-4" />
              <span className="font-semibold">PCDB Data Protected:</span>
              <span>Boiler information from PCDB database is read-only. Toggle manual entry to edit fields.</span>
            </div>
          )}
        </div>
      )}
      
      <BuiltInFormRenderer
        schema={heatingSystemSchema}
        formData={allFormData}
        photos={photos}
        onFormDataChange={(fieldId, value) => {
          const baseFieldId = fieldId.replace(/_photos$/, '');
          const isCustomField = customFields.some(f => f.id === baseFieldId);
          
          if (isCustomField) {
            handleCustomFieldChange(fieldId, value);
          } else {
            handleFormDataChange(fieldId, value);
          }
        }}
        onPhotosChange={handlePhotosChange}
        onSubmit={handleSubmit}
        customFields={customFields}
        hiddenFields={hiddenFields}
        hiddenSections={hiddenSections}
        sectionOrder={sectionOrder}
        builtInFieldOrder={builtInFieldOrder}
        navigationButtons={{
          previous: onNavigateToPrevious ? {
            label: '← Previous',
            onClick: onNavigateToPrevious
          } : undefined,
          next: onNavigateToNext ? {
            label: 'Next →',
            onClick: onNavigateToNext
          } : undefined
        }}
        onGalleryOpen={handleGalleryOpen}
        selectedGallery={selectedGallery}
        formIcon={<Flame className="w-8 h-8" />}
      />
      
      {selectedGallery && photos[selectedGallery] && (
        <PhotoGalleryModal
          isOpen={true}
          onClose={handleGalleryClose}
          photos={photos[selectedGallery].map((photo: any) => ({
            id: photo.displayId || photo.id,
            url: photo.url || photo.dataUrl,
            uploading: photo.uploading,
            error: photo.error
          }))}
          sectionTitle={`${selectedGallery} Photos`}
          onDeletePhoto={(photoId) => removePhoto(selectedGallery, photoId)}
          onUpdateNote={(photoId, note) => {
            setPhotos(current => ({
              ...current,
              [selectedGallery]: current[selectedGallery].map(p =>
                (p.displayId || p.id) === photoId ? { ...p, note } : p
              )
            }));
            
            if (onDataUpdate) {
              onDataUpdate((prevData) => ({
                ...prevData,
                heating_system: {
                  ...prevData.heating_system,
                  photos: {
                    ...photos,
                    [selectedGallery]: (photos[selectedGallery] || []).map(p =>
                      (p.displayId || p.id) === photoId ? { ...p, note } : p
                    )
                  }
                }
              }), 'heating_system');
            }
          }}
        />
      )}
    </>
  );
};
